import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';
import { privacyContent } from '@/data/legal/privacyContent';

const { FiChevronLeft } = FiIcons;

export default function Privacy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] flex flex-col overflow-y-auto no-scrollbar">
      <div className="grain-overlay" />
      <div className="vignette" />

      {/* 返回按鈕 */}
      <header className="fixed top-0 left-0 z-50 p-6 md:p-10 safe-pt">
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors"
        >
          <div className="w-10 h-10 rounded-full border border-white/10 group-hover:border-amber-400/50 flex items-center justify-center bg-white/[0.02] backdrop-blur-sm transition-all shadow-md">
            <SafeIcon icon={FiChevronLeft} className="text-base group-hover:-translate-x-0.5 transition-transform" />
          </div>
          <span className="text-[10px] tracking-[0.3em] uppercase hidden sm:block">Back</span>
        </button>
      </header>

      {/* 主要內容 */}
      <div className="flex-1 flex items-start justify-center px-6 pt-32 pb-20 safe-pb">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full max-w-3xl"
        >
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h1 className="text-3xl md:text-4xl text-amber-400 font-serif tracking-widest mb-4 text-center">
              {privacyContent.title}
            </h1>
            <p className="text-gray-500 text-xs tracking-wider text-center mb-10">
              {privacyContent.titleEn}
            </p>

            <div className="space-y-8 text-gray-300 leading-relaxed">
              {/* 生效日期 */}
              <section>
                <p className="text-amber-400/80 text-sm mb-2">生效日期: {privacyContent.effectiveDate}</p>
                <p className="text-gray-400 text-sm">{privacyContent.introduction}</p>
              </section>

              {/* 動態渲染所有章節 */}
              {privacyContent.sections.map((section) => (
                <section key={section.id}>
                  <h2 className="text-xl text-amber-400 font-serif mb-3">
                    {section.id}. {section.title}
                  </h2>
                  
                  {section.content && (
                    <p 
                      className="text-sm text-gray-400 mb-2" 
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  )}

                  {section.list && (
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-400 ml-4">
                      {section.list.map((item, idx) => (
                        <li key={idx} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  )}

                  {section.subsections && section.subsections.map((sub, idx) => (
                    <div key={idx} className="mt-4 ml-4">
                      <h3 className="text-amber-300 text-base mb-2">{sub.subtitle}</h3>
                      {sub.content && <p className="text-sm text-gray-400 mb-2">{sub.content}</p>}
                      {sub.list && (
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-400 ml-4">
                          {sub.list.map((item, i) => (
                            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                          ))}
                        </ul>
                      )}
                      {sub.note && (
                        <p className="text-xs text-amber-300/60 mt-2 italic">{sub.note}</p>
                      )}
                    </div>
                  ))}

                  {section.warning && (
                    <p className="text-xs text-red-300/60 mt-2 italic">{section.warning}</p>
                  )}

                  {section.contact && (
                    <div className="mt-3 text-sm text-gray-400">
                      {typeof section.contact === 'string' ? (
                        <p>{section.contact}</p>
                      ) : (
                        <>
                          <p>Email: <a href={`mailto:${section.contact.email}`} className="text-amber-400 hover:text-amber-300">{section.contact.email}</a></p>
                          <p>網站: <a href={section.contact.website} className="text-amber-400 hover:text-amber-300">{section.contact.website}</a></p>
                          {section.contact.address && <p>地址: {section.contact.address}</p>}
                        </>
                      )}
                    </div>
                  )}
                </section>
              ))}

              {/* 底部聲明 */}
              <div className="border-t border-white/10 pt-6 mt-10">
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  {privacyContent.footer}<br />
                  最後更新日期: {privacyContent.effectiveDate}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}